import {
  ProblemSetProgress,
  ProgressStatus,
  SubmissionStatus,
  UserRole,
  Visibility,
} from "@prisma/client";
import { apiSlice, PROBLEM_SET_TAG, PROBLEM_SETS_TAG } from "../api";

export interface ProblemSetResponse {
  num: string;
  name: string;
  author: { id: string; name: string; role: UserRole };
  description?: string;
  problems: string[];
  views: number;
  likes: number;
  userLiked?: boolean;
  userStarred?: boolean;
  userCompleted?: boolean;
  userProgress?: { id: string; problemOrder: ProblemOrderItem[] };
  completedCount?: number;
  problemCount: number;
  averageRank: number;
  createdAt: string;
}

export interface GetPSetsResponse {
  currentPage: number;
  limit: number;
  totalPages: number;
  totalProblems: number;
  problemSets: ProblemSetResponse[];
}

export interface PSetProblem {
  num: string;
  rank: number;
  position: number;
  initial: string;
}

export interface LeaderboardResponse {
  user: {
    id: string;
    name: string;
  };
  completedAt: string;
  durationMs: string;
  score: number;
  completionCount: number;
}

export interface PSetResponse extends Omit<ProblemSetResponse, "problems"> {
  completedCount: number;
  attemptedCount: number;
  views: number;
  likes: number;
  userLiked: boolean;
  userCompletions: number;
  problems: PSetProblem[];
  leaderboard?: LeaderboardResponse[];
}

export interface PSetProblemResponse {
  problem: PSetProblem;
  progress: ProblemSetProgress;
}

export interface ProblemOrderItem {
  problemNum: string;
  status?: SubmissionStatus;
}

export interface CreatePSetProgressResponse {
  id: string;
  createdAt: string;
  status: ProgressStatus;
  problemOrder: ProblemOrderItem[];
  problemSet: {
    name: string;
    num: string;
  };
}

export interface PSetProgressResponse {
  id: string;
  problemSet: { name: string; id: string };
  problemOrder: ProblemOrderItem[];
  status: ProgressStatus;
}

export interface CreatePSetRequest {
  name: string;
  description: string;
  sgf: string;
  visibility: Visibility;
  teamSlugs?: string[];
}

const problemSetApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPSets: builder.query<GetPSetsResponse, string>({
      query: (search) => `problems/sets?${search}`,
      providesTags: (result) =>
        result?.problemSets
          ? [
              ...result.problemSets.map((pset) => ({
                type: PROBLEM_SET_TAG,
                id: pset.num,
              })),
            ]
          : [PROBLEM_SETS_TAG],
    }),
    getPSet: builder.query<PSetResponse, string>({
      query: (num) => `problems/sets/${num}?leaderboard=true`,
      providesTags: (result, error, arg) => [
        { type: PROBLEM_SET_TAG, id: arg },
      ],
    }),
    getPSetProgress: builder.query<PSetProgressResponse, string>({
      query: (num) => `problems/sets/${num}/progress`,
      providesTags: (result, error, arg) => [
        { type: PROBLEM_SET_TAG, id: arg },
      ],
    }),
    createPSetProgress: builder.mutation<
      CreatePSetProgressResponse,
      { id: string; randomize?: boolean }
    >({
      query: ({ id, randomize }) => ({
        url: `problems/sets/${id}/progress`,
        method: "POST",
        body: { randomize },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: PROBLEM_SET_TAG, id: arg.id },
      ],
    }),
    endPSetProgress: builder.mutation<void, string>({
      query: (num) => ({
        url: `problems/sets/${num}/progress/end`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: PROBLEM_SET_TAG, id: arg },
      ],
    }),
    pSetLike: builder.mutation<{ liked: boolean }, string>({
      query: (num) => ({
        url: `problems/sets/${num}/like`,
        method: "POST",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: PROBLEM_SET_TAG, id: arg },
      ],
    }),
    pSetStar: builder.mutation<{ starred: boolean }, string>({
      query: (num) => ({
        url: `problems/sets/${num}/star`,
        method: "POST",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: PROBLEM_SET_TAG, id: arg },
      ],
    }),

    createPSet: builder.mutation<{ problemSetNum: string }, CreatePSetRequest>({
      query: (body) => ({
        url: "problems/sets/new",
        method: "POST",
        body,
      }),
      invalidatesTags: [PROBLEM_SETS_TAG],
    }),

    deletePSet: builder.mutation<void, string>({
      query: (num) => ({
        url: `problems/sets/${num}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: PROBLEM_SET_TAG, id: arg },
        PROBLEM_SETS_TAG,
      ],
    }),
  }),
});

export const {
  useGetPSetsQuery,
  useGetPSetQuery,
  useGetPSetProgressQuery,
  useCreatePSetProgressMutation,
  useEndPSetProgressMutation,
  usePSetLikeMutation,
  usePSetStarMutation,

  useCreatePSetMutation,
  useDeletePSetMutation,
} = problemSetApiSlice;
